import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { ShieldCheck, Star, ArrowRight, CheckCircle2 } from "lucide-react";
import { heroBadges, stats } from "../mock";

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
      <div className="absolute inset-0 -z-0 opacity-[0.35] [background-image:radial-gradient(circle_at_1px_1px,_rgba(11,36,71,0.12)_1px,_transparent_0)] [background-size:22px_22px]" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 md:pt-24 md:pb-28 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 shadow-sm mb-6">
              <ShieldCheck className="w-3.5 h-3.5 text-[#0B2447]" />
              No removal, no fee guarantee
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B2447] leading-[1.05] tracking-tight">
              We remove defamatory reviews— <span className="text-slate-500">or you don't pay.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Unfair reviews and malicious posts can cost you customers, candidates and clients. Our specialists get them taken down from the platforms that matter, with zero upfront fees.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button className="bg-[#0B2447] hover:bg-[#122e5f] text-white h-12 px-6 text-base">
                  Remove my content <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="#how">
                <Button variant="outline" className="h-12 px-6 text-base border-slate-300 text-[#0B2447]">
                  See how it works
                </Button>
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
              {heroBadges.map((b) => (
                <div key={b} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {b}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <ReviewCard />
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl bg-white border border-slate-200 p-5 shadow-sm">
              <div className="text-2xl md:text-3xl font-bold text-[#0B2447]">{s.number}</div>
              <div className="text-xs md:text-sm text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReviewCard() {
  return (
    <div className="relative bg-white rounded-2xl border border-slate-200 shadow-xl p-6">
      <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
        <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-[#0B2447]">M&amp;F</div>
        <div>
          <div className="font-semibold text-[#0B2447] text-sm">Marbury &amp; Finch LLP</div>
          <div className="text-xs text-slate-500">Law firm · Google Business Profile</div>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        <ReviewRow initials="MR" name="Megan R." stars={5} text="Compassionate, sharp and got my case dismissed." removed={false} />
        <ReviewRow initials="?" name="Anonymous" stars={1} text="Total scam!! Never even hired this firm…" removed />
        <ReviewRow initials="DK" name="David K." stars={5} text="Clear advice through a difficult divorce." removed={false} />
        <ReviewRow initials="?" name="Anonymous" stars={1} text="Crooks in suits. Do not trust these lawyers…" removed />
      </div>
      <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-50 p-3">
        <div className="text-xs text-slate-500">Profile rating</div>
        <div className="text-sm"><span className="line-through text-slate-400 mr-2">3.3</span><span className="font-bold text-[#0B2447]">4.8 ★</span></div>
      </div>
    </div>
  );
}

function ReviewRow({ initials, name, stars, text, removed }) {
  return (
    <div className={`py-3 flex gap-3 ${removed ? "opacity-60" : ""}`}>
      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600 shrink-0">{initials}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold text-[#0B2447]">{name}</div>
          <div className="flex items-center">{Array.from({ length: 5 }).map((_, i) => (<Star key={i} className={`w-3 h-3 ${i < stars ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />))}</div>
        </div>
        <div className={`text-xs mt-0.5 ${removed ? "line-through text-slate-500" : "text-slate-600"}`}>{text}</div>
        {removed && (
          <div className="inline-block mt-1 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">Removed</div>
        )}
      </div>
    </div>
  );
}
